import { toMinutes } from './time';
import type { CalendarEvent } from '../types/db';

/** Shared geometry of the agenda arc, so orbit cards can keep clear of it. */
export const AGENDA_T0 = 7 * 60;
export const AGENDA_T1 = 19 * 60;
export const AGENDA_A0 = (-200 * Math.PI) / 180;
export const AGENDA_A1 = (20 * Math.PI) / 180;

export const agendaAngleAt = (min: number) =>
  AGENDA_A0 + ((Math.min(AGENDA_T1, Math.max(AGENDA_T0, min)) - AGENDA_T0) / (AGENDA_T1 - AGENDA_T0)) * (AGENDA_A1 - AGENDA_A0);

export const agendaRadius = (R: number) => R * 1.16;

/** Rectangle relative to Anna's core */
export interface Box {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

export interface AgendaLabel {
  id: string;
  /** Anchor relative to Anna's core */
  x: number;
  y: number;
  tx: '-100%' | '0%' | '-50%';
  ty: '-100%' | '0%' | '-50%';
  alignRight: boolean;
  box: Box;
  text: string;
  isNext: boolean;
}

const CHAR_W = 12.4;
const LINE_H = 30;

export function agendaLabels(events: CalendarEvent[], R: number, nextId?: string): AgendaLabel[] {
  const r = agendaRadius(R) + 30;
  return events
    .filter((e) => e.kind !== 'focus')
    .map((e) => {
      const mid = agendaAngleAt((toMinutes(e.start) + toMinutes(e.end)) / 2);
      const x = r * Math.cos(mid);
      const y = r * Math.sin(mid);
      const c = Math.cos(mid);
      const s = Math.sin(mid);
      const tx = c < -0.3 ? '-100%' : c > 0.3 ? '0%' : '-50%';
      const ty = s < -0.3 ? '-100%' : s > 0.3 ? '0%' : '-50%';
      const isNext = e.id === nextId;
      const text = `${e.start} ${e.title.split(',')[0]}`;
      const w = text.length * CHAR_W + 8;
      const h = isNext ? LINE_H * 2 : LINE_H;
      const x0 = tx === '-100%' ? x - w : tx === '0%' ? x : x - w / 2;
      const y0 = ty === '-100%' ? y - h : ty === '0%' ? y : y - h / 2;
      return { id: e.id, x, y, tx, ty, alignRight: c < -0.3, box: { x0, x1: x0 + w, y0, y1: y0 + h }, text, isNext };
    });
}
